import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../custom/customDropdown";
import { AuthContext } from "../../Header/auth - context";
import "./Fakture.css";

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const iznos = (v) =>
    v === null || v === undefined || v === ""
        ? "0,00"
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const praznoZaglavlje = {
    id: null, brojIzvoda: null, godina: new Date().getFullYear(),
    id_tekuci_racun: null, datumIzvoda: new Date(), valuta: null,
    kurs: null, kursValuta: null,
    prethodnoStanje: null, novoStanje: null, dugovniPromet: null, potrazniPromet: null,
    /* kompenzacija se NE unosi na ovom ekranu — nema polja za nju. Vrednost se
       samo čita i vraća nazad pri snimanju, da zastavica upisana sa druge strane
       ne bude obrisana. Prava kompenzacija je zaseban dokument (u CYCLE-u
       tblKompenzacijaHdr/Rw), a ne kvačica na izvodu banke. */
    kompenzacija: false, napomena: "", zakljucan: false,
};

const praznaStavka = {
    id_stavka: null, id_klijent: null, klijentNaziv: "", duguje: null, potrazuje: null,
    brojRacuna: "", sifraPlacanja: "", opisPlacanja: "",
    modelOdobrenja: "", pozivOdobrenja: "", modelZaduzenja: "", pozivZaduzenja: "",
    vrstaStavke: null, id_vozac: null, id_vrsta_troska: null, id_povezano_lice: null,
    opisKUF: "", napomena: "",
};

const Izvodi = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [prikaz, setPrikaz] = useState("PREGLED");   // PREGLED | IZVOD
    const [sifarnici, setSifarnici] = useState({});

    /* pregled izvoda */
    const [lista, setLista] = useState([]);
    const [godina, setGodina] = useState(new Date().getFullYear());
    const [filterRacun, setFilterRacun] = useState(null);
    /* Dve tabele, dva filtera. Jedan zajednicki state znaci da tekst ukucan na
       spisku izvoda ostane aktivan i na stavkama otvorenog izvoda, pa deo redova
       tiho nestane bez vidljivog razloga. */
    const [globalFilter, setGlobalFilter] = useState(null);
    const [filterStavki, setFilterStavki] = useState(null);
    const [ucitava, setUcitava] = useState(false);

    /* jedan izvod */
    const [zaglavlje, setZaglavlje] = useState({ ...praznoZaglavlje });
    const [stavke, setStavke] = useState([]);
    const [izabranaStavka, setIzabranaStavka] = useState(null);
    const [filterKlijent, setFilterKlijent] = useState(null);
    const [cuva, setCuva] = useState(false);
    /* Zaglavlje zauzima tri reda polja i gura donju tabelu ispod ekrana.
       Skupljeno ostavlja samo prvi red (broj, godina, račun, stanja). */
    const [zaglavljeOtvoreno, setZaglavljeOtvoreno] = useState(true);
    /* Datum liste sa koje je kurs uzet — prikazuje se samo kad NIJE sa datuma
       izvoda, da se ne podmetne kurs drugog dana kao da je taj. */
    const [kursDatum, setKursDatum] = useState(null);

    /* donja tabela: otvorene fakture partnera */
    const [otvorene, setOtvorene] = useState([]);
    const [izborZaSravnjenje, setIzborZaSravnjenje] = useState({});   // { id_dokument: iznos }
    const [napomeneSravnjenja, setNapomeneSravnjenja] = useState({}); // { id_dokument: tekst }
    const [ucitavaOtvorene, setUcitavaOtvorene] = useState(false);
    const [sravnjenja, setSravnjenja] = useState([]);

    /* modali */
    const [stavkaModal, setStavkaModal] = useState(null);
    const [kontaktModal, setKontaktModal] = useState(null);           // vezivanje računa za partnera
    const [uvoz, setUvoz] = useState(null);
    const fajlRef = useRef(null);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 5000 });

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/izvod/" + putanja, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    const greska = (e) => poruka("error", t("common.greska"), e.message);

    useEffect(() => {
        posalji("Init").then(setSifarnici).catch(() => {});
        // eslint-disable-next-line
    }, []);

    const ucitajPregled = () => {
        setUcitava(true);
        posalji("Pregled", { godina, id_tekuci_racun: filterRacun })
            .then((d) => setLista(d || []))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitajPregled(); /* eslint-disable-next-line */ }, []);

    const otvoriIzvod = (id) => {
        posalji("Get", { id })
            .then((d) => {
                const z = d.zaglavlje || {};
                setZaglavlje({
                    id: z.id, brojIzvoda: z.BrojIzvoda, godina: z.Godina,
                    id_tekuci_racun: z.id_tekuci_racun,
                    datumIzvoda: z.DatumIzvoda ? new Date(z.DatumIzvoda) : new Date(),
                    valuta: z.Valuta, kurs: z.Kurs, kursValuta: z.KursValuta,
                    prethodnoStanje: z.PrethodnoStanje, novoStanje: z.NovoStanje,
                    dugovniPromet: z.DugovniPromet, potrazniPromet: z.PotrazniPromet,
                    kompenzacija: !!z.Kompenzacija, napomena: z.Napomena || "",
                    zakljucan: !!z.Zakljucan,
                });
                setStavke(d.stavke || []);
                setIzabranaStavka(null);
                setFilterKlijent(null);
                setOtvorene([]);
                setIzborZaSravnjenje({});
                setNapomeneSravnjenja({});
                setSravnjenja([]);
                setPrikaz("IZVOD");
            })
            .catch(greska);
    };

    /* Jedinstvenost broja je po (firma, račun, godina), a i banka numeriše po
       računu — zato se predlog mora ponoviti kad se račun izabere. Bez toga
       ostaje broj izračunat pre izbora računa i snimanje pukne porukom da izvod
       sa tim brojem već postoji. */
    const predloziBroj = (idRacun, godinaIzvoda) =>
        posalji("NoviBroj", { godina: godinaIzvoda, id_tekuci_racun: idRacun })
            .then((d) => setZaglavlje((p) => ({ ...p, brojIzvoda: d.BrojIzvoda })))
            .catch(() => {});

    const noviIzvod = () => {
        setZaglavlje({ ...praznoZaglavlje });
        setStavke([]);
        setIzabranaStavka(null);
        setFilterKlijent(null);
        setOtvorene([]);
        setIzborZaSravnjenje({});
        setNapomeneSravnjenja({});
        setSravnjenja([]);
        setPrikaz("IZVOD");
        /* Broj se predlaže odmah — operater ga ne traži ručno po spisku. */
        predloziBroj(null, new Date().getFullYear());
    };

    /* Kurs se povlači sa NBS liste pri svakoj promeni datuma ili valute — ta dva
       podatka ga i određuju. Zaključan izvod se ne dira. */
    const povuciKurs = (datum, valuta) => {
        if (!datum || !valuta || zaglavlje.zakljucan) return;
        posalji("Kurs", { datum: toUtcIso(datum), valuta })
            .then((d) => {
                setZaglavlje((p) => ({
                    ...p,
                    kurs: d.Kurs ?? p.kurs,
                    kursValuta: d.KursValuta ?? p.kursValuta,
                }));
                setKursDatum(Number(d.KursSaDrugogDana) === 1 ? d.DatumKursa : null);
            })
            .catch(() => {});
    };

    const promeniDatum = (datum) => {
        setZaglavlje((p) => ({ ...p, datumIzvoda: datum }));
        povuciKurs(datum, zaglavlje.valuta);
    };

    const promeniValutu = (valuta) => {
        setZaglavlje((p) => ({ ...p, valuta }));
        povuciKurs(zaglavlje.datumIzvoda, valuta);
    };

    const promeniRacun = (idRacun) => {
        setZaglavlje((p) => ({ ...p, id_tekuci_racun: idRacun }));
        /* Samo za nov izvod — postojećem se broj ne dira. */
        if (!zaglavlje.id && idRacun) predloziBroj(idRacun, zaglavlje.godina || new Date().getFullYear());
    };

    const zakljucaj = () => {
        if (!zaglavlje.id) return;
        const novo = zaglavlje.zakljucan ? 0 : 1;
        confirmDialog({
            message: novo ? t("izvod.potvrdaZakljucavanja") : t("izvod.potvrdaOtkljucavanja"),
            header: novo ? t("izvod.zakljucaj") : t("izvod.otkljucaj"),
            icon: "pi pi-lock",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Zakljucaj", { id: zaglavlje.id, zakljucan: novo })
                .then((d) => {
                    poruka("success", t("common.sacuvano"), d.message);
                    setZaglavlje((p) => ({ ...p, zakljucan: !!Number(d.Zakljucan) }));
                    ucitajPregled();
                })
                .catch(greska),
        });
    };

    const obrisiIzvod = () => {
        if (!zaglavlje.id) return;
        confirmDialog({
            message: t("izvod.potvrdaBrisanjaIzvoda"),
            header: t("izvod.brisanjeIzvoda"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Delete", { id: zaglavlje.id })
                .then((d) => {
                    poruka("success", t("common.sacuvano"), d.message);
                    setPrikaz("PREGLED");
                    ucitajPregled();
                })
                .catch(greska),
        });
    };

    const sacuvajZaglavlje = () => {
        setCuva(true);
        posalji("Save", {
            ...zaglavlje,
            datumIzvoda: toUtcIso(zaglavlje.datumIzvoda),
            kompenzacija: zaglavlje.kompenzacija ? 1 : 0,
        })
            .then((d) => {
                poruka("success", t("common.sacuvano"), d.message);
                setZaglavlje((p) => ({ ...p, id: d.ID }));
                ucitajPregled();
            })
            .catch(greska)
            .finally(() => setCuva(false));
    };

    /* ── stavke ── */
    /* Vraca svezu listu, ne samo sto je upise u state: posle sravnjenja izabrana
       stavka mora da se zameni svezim redom (Preostalo se promenilo), a state u
       istom prolazu jos nije vidljiv. */
    const osveziStavke = (idIzvoda) =>
        posalji("Get", { id: idIzvoda || zaglavlje.id })
            .then((d) => { const sveze = d.stavke || []; setStavke(sveze); return sveze; })
            .catch(() => []);

    const sacuvajStavku = () => {
        if (!zaglavlje.id) { poruka("warn", t("common.upozorenje"), t("izvod.prvoSacuvajIzvod")); return; }
        posalji("StavkaSave", { ...stavkaModal, id_izvod: zaglavlje.id })
            .then((d) => {
                poruka("success", t("common.sacuvano"), d.message);
                setStavkaModal(null);
                osveziStavke();
            })
            .catch(greska);
    };

    const obrisiStavku = () => {
        if (!izabranaStavka) return;
        confirmDialog({
            message: t("izvod.potvrdaBrisanjaStavke"),
            header: t("izvod.brisanjeStavke"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("StavkaDelete", { id: izabranaStavka.id })
                .then((d) => {
                    poruka("success", t("common.sacuvano"), d.message);
                    setIzabranaStavka(null); setOtvorene([]); setSravnjenja([]);
                    osveziStavke();
                })
                .catch(greska),
        });
    };

    /* ── izbor stavke → otvorene fakture partnera ──
       Smer se izvodi iz same stavke: uplata (Potražuje) nudi izlazne fakture,
       isplata (Duguje) ulazne. Ponuditi oboje znači pozvati operatera da
       uplatom kupca zatvori naš dug prema dobavljaču. */
    const izaberiStavku = (s) => {
        setIzabranaStavka(s);
        setIzborZaSravnjenje({});
        setNapomeneSravnjenja({});
        if (!s) { setOtvorene([]); setSravnjenja([]); return; }

        /* Bez partnera se NE nudi spisak. Upit bez klijenta vraca sve otvorene
           fakture firme, a bas uvezene stavke su te koje najcesce nemaju
           prepoznatog partnera — to je poziv da se zatvori tudja faktura.
           Vec upisana sravnjenja se i dalje prikazuju, da se mogu ponistiti. */
        if (!s.id_klijent) {
            setOtvorene([]);
            posalji("Sravnjenja", { id_izvod_stavka: s.id })
                .then((sr) => setSravnjenja(sr || []))
                .catch(() => {});
            return;
        }

        const smer = Number(s.Potrazuje) > 0 ? "ULAZ" : "IZLAZ";
        setUcitavaOtvorene(true);
        Promise.all([
            posalji("Otvorene", { id_klijent: s.id_klijent, smer, valuta: s.Valuta }),
            posalji("Sravnjenja", { id_izvod_stavka: s.id }),
        ])
            .then(([o, sr]) => { setOtvorene(o || []); setSravnjenja(sr || []); })
            .catch(greska)
            .finally(() => setUcitavaOtvorene(false));
    };

    /* Posle svake radnje koja menja sravnjenja. Bez ovoga izabrana stavka ostaje
       stari objekat sa starim Preostalo, pa gornja granica sravnjenja laze i
       kvacica nudi iznos koji je vec potrosen. */
    const osveziIzabranu = (sveze) => {
        if (!izabranaStavka) return;
        const svez = (sveze || []).find((x) => String(x.id) === String(izabranaStavka.id));
        if (svez) izaberiStavku(svez); else setIzabranaStavka(null);
    };

    /* Preostalo na stavci izvoda — gornja granica svega što se sme sravniti. */
    const preostaloNaStavci = Number(izabranaStavka?.Preostalo ?? 0);

    const predlozi = () => {
        if (!izabranaStavka) return;
        posalji("Predlozi", { id_izvod_stavka: izabranaStavka.id })
            .then((d) => {
                if (!d?.length) { poruka("info", t("izvod.predlozi"), t("izvod.nemaPredloga")); return; }
                setOtvorene(d);
                /* Prvi predlog se čekira do visine preostalog iznosa stavke —
                   ostalo operater dopunjuje. Nikad se ne sravnjuje samo. */
                const prvi = d[0];
                setIzborZaSravnjenje({
                    [prvi.id_dokument]: Math.min(Number(prvi.IznosZaSravnjenje || 0), preostaloNaStavci),
                });
                poruka("success", t("izvod.predlozi"), t("izvod.nadjenoPredloga", { broj: d.length }));
            })
            .catch(greska);
    };

    /* Kvačica popunjava iznos do preostalog na stavci; skidanje ga briše.
       Iznos se i dalje sme ručno prekucati — kvačica samo prati ima li iznosa. */
    const prekidacReda = (red, cekirano) =>
        setIzborZaSravnjenje((p) => {
            const n = { ...p };
            if (!cekirano) { delete n[red.id_dokument]; return n; }
            const vecUzeto = Object.entries(p)
                .filter(([k]) => String(k) !== String(red.id_dokument))
                .reduce((s, [, v]) => s + (Number(v) || 0), 0);
            const slobodno = Math.max(preostaloNaStavci - vecUzeto, 0);
            n[red.id_dokument] = Math.min(Number(red.IznosZaSravnjenje || 0), slobodno);
            return n;
        });

    const zbirIzbora = useMemo(
        () => Object.values(izborZaSravnjenje).reduce((s, v) => s + (Number(v) || 0), 0),
        [izborZaSravnjenje]
    );

    const sravni = () => {
        if (!izabranaStavka) { poruka("warn", t("common.upozorenje"), t("izvod.izaberiStavku")); return; }
        const izabrano = Object.entries(izborZaSravnjenje)
            .filter(([, v]) => Number(v) > 0)
            .map(([id, v]) => {
                const red = otvorene.find((o) => String(o.id_dokument) === String(id));
                return {
                    TipDokumenta: red?.TipDokumenta,
                    id_dokument: Number(id),
                    Iznos: Number(v),
                    Napomena: napomeneSravnjenja[id] || null,
                };
            });
        if (!izabrano.length) { poruka("warn", t("common.upozorenje"), t("izvod.izaberiFakture")); return; }

        posalji("Sravni", { id_izvod_stavka: izabranaStavka.id, stavke: izabrano })
            .then((d) => {
                poruka("success", t("izvod.sravni"), d.message);
                setIzborZaSravnjenje({});
                setNapomeneSravnjenja({});
                osveziStavke().then(osveziIzabranu);
            })
            .catch(greska);
    };

    const ponisti = (red) => {
        confirmDialog({
            message: t("izvod.potvrdaPonistenja"),
            header: t("izvod.ponisti"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Ponisti", { id: red.id })
                .then((d) => {
                    poruka("success", t("izvod.ponisti"), d.message);
                    osveziStavke().then(osveziIzabranu);
                })
                .catch(greska),
        });
    };

    /* ── "Novi kontakt": broj računa iz banke veže se za partnera, pa ga
          sledeći uvoz prepozna sam. ── */
    const veziRacun = () => {
        posalji("VeziRacun", {
            id_klijent: kontaktModal.id_klijent,
            tekuciRacun: kontaktModal.tekuciRacun,
            nazivBanke: kontaktModal.nazivBanke,
            id_izvod_stavka: kontaktModal.id_izvod_stavka,
        })
            .then((d) => {
                poruka("success", t("izvod.poveziRacun"), d.message);
                setKontaktModal(null);
                osveziStavke();
            })
            .catch(greska);
    };

    /* ── uvoz XML ──
       Fajl se čita u pregledaču i šalje kao tekst — mali je, a tako nema
       privremenih fajlova na serveru. Prvo PREGLED (bez upisa), pa potvrda. */
    const izaberiFajl = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const citac = new FileReader();
        citac.onload = () => {
            const xml = String(citac.result || "");
            posalji("ImportPregled", { xml })
                .then((d) => setUvoz({ xml, pregled: d }))
                .catch(greska);
        };
        citac.readAsText(f, "utf-8");
        e.target.value = "";
    };

    const potvrdiUvoz = () => {
        /* Valuta se NE salje odavde: uvoz se pokrece sa spiska izvoda, gde je
           zaglavlje jos prazno, pa bi svaki uvezen izvod ostao bez valute.
           Oznaku iz fajla (curdef) mapira procedura. */
        posalji("ImportPotvrdi", {
            xml: uvoz.xml,
            id_tekuci_racun: uvoz.pregled.id_tekuci_racun,
        })
            .then((d) => {
                poruka("success", t("izvod.uvoz"),
                    t("izvod.uvozRezultat", { uvezeno: d.Uvezeno, preskoceno: d.Preskoceno }));
                setUvoz(null);
                ucitajPregled();
                if (d.ID) otvoriIzvod(d.ID);
            })
            .catch(greska);
    };

    /* ── boja reda = stanje stavke (legenda iznad tabele) ── */
    const klasaStavke = (s) => {
        if (s.Sravnjeno) return { "iz-red-sravnjen": true };
        if (s.id_povezano_lice || s.id_vozac) return { "iz-red-vozac": true };
        if (s.id_vrsta_troska) return { "iz-red-trosak": true };
        return { "iz-red-nesravnjen": true };
    };

    /* Filter po klijentu radi nad već učitanim stavkama — izvod je jedan
       dokument, nema razloga ići na server zbog suženja spiska. */
    const prikazaneStavke = useMemo(
        () => (stavke || []).filter((s) => !filterKlijent || String(s.id_klijent) === String(filterKlijent)),
        [stavke, filterKlijent]
    );

    const zbirStavki = useMemo(() => ({
        duguje: prikazaneStavke.reduce((s, r) => s + Number(r.Duguje || 0), 0),
        potrazuje: prikazaneStavke.reduce((s, r) => s + Number(r.Potrazuje || 0), 0),
    }), [prikazaneStavke]);

    /* ── Excel export pregleda izvoda ── */
    const exportExcelPregled = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Izvodi");
        const headers = [
            t("izvod.broj"), t("izvod.godina"), t("izvod.datum"), t("izvod.ziroRacun"),
            t("izvod.prethodnoStanje"), t("izvod.dugovniPromet"), t("izvod.potrazniPromet"),
            t("izvod.novoStanje"), t("izvod.stavki"), t("izvod.nesravnjenih"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (lista || []).forEach((r) => {
            const row = worksheet.addRow([
                r.BrojIzvoda, r.Godina, r.DatumIzvoda || "", r.TekuciRacun || "",
                Number(r.PrethodnoStanje || 0), Number(r.DugovniPromet || 0),
                Number(r.PotrazniPromet || 0), Number(r.NovoStanje || 0),
                r.BrojStavki, r.Nesravnjenih,
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("izvod.tabIzvodi")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const headerPregleda = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcelPregled} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    /* ── Excel export stavki izvoda ── */
    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Izvod");
        const headers = [
            t("izvod.klijent"), t("izvod.brStavke"), t("izvod.sifraPlacanja"),
            t("izvod.dugujePlaceno"), t("izvod.potrazujeNaplaceno"), t("izvod.pozivNaBroj"),
            t("izvod.opisPlacanja"), t("izvod.povezanoLice"), t("izvod.opisKUF"), t("nova.napomena"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        prikazaneStavke.forEach((r) => {
            const row = worksheet.addRow([
                r.KlijentIzSifarnika || r.KlijentNaziv || "", r.BrojStavke, r.SifraPlacanja || "",
                Number(r.Duguje || 0), Number(r.Potrazuje || 0), r.PozivOdobrenja || "",
                r.OpisPlacanja || "", r.PovezanoLice || "", r.OpisKUF || "", r.Napomena || "",
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        const zbirRed = worksheet.addRow(["", "", t("izvod.ukupno"), zbirStavki.duguje, zbirStavki.potrazuje]);
        zbirRed.font = { bold: true };
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("izvod.izvodBr")} ${zaglavlje.brojIzvoda || ""} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    /* Trakica sa karticama je ista kao na ulaznim fakturama i na Izveštajima:
       PrimeReact dugmad u .ulazne-trakica-kartica, a aktivno nosi
       id="activeButton". Taj id je nosilac stila kroz celu aplikaciju — hvata ga
       i dark-theme.css, pa svaka sopstvena traka tabova ostaje neobojena u
       tamnoj temi.

       Prelazak na „Izvodi" bez otvorenog izvoda otvara nov — inače bi tab vodio
       na praznu formu bez broja. */
    const tabovi = [
        { label: t("izvod.tabPregled") },
        { label: t("izvod.tabIzvodi") },
    ];

    const promeniTab = (index) => {
        if (index === 0) { setPrikaz("PREGLED"); ucitajPregled(); return; }
        if (zaglavlje.id) setPrikaz("IZVOD");
        else noviIzvod();
    };

    const trakaTabova = (aktivan) => (
        <div className="ulazne-trakica-kartica izvodi-traka">
            {tabovi.map((x, i) => (
                <Button key={x.label} label={x.label}
                    id={aktivan === i ? "activeButton" : undefined}
                    style={{ fontSize: "smaller" }}
                    className="mt-3 zaposleniShift"
                    onClick={() => promeniTab(i)} />
            ))}
        </div>
    );

    /* Zbirovi stoje u footeru tabele, tačno ispod svojih kolona — na papirnom
       izvodu se čitaju vertikalno, pa se tako i ovde moraju poklopiti. */
    const footerStavki = (
        <ColumnGroup>
            <Row>
                <Column footer={t("izvod.ukupno")} colSpan={3} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbirStavki.duguje)} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbirStavki.potrazuje)} footerStyle={{ textAlign: "right" }} />
                <Column footer="" colSpan={5} />
            </Row>
        </ColumnGroup>
    );

    const headerStavki = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setFilterStavki(e.target.value)} />
        </div>
    );

    // ══════════════════════════ PREGLED IZVODA ══════════════════════════
    if (prikaz === "PREGLED") {
        return (
            <div className="col-12">
                <Toast ref={toast} /><ConfirmDialog />
                <Tooltip target=".export-buttons>button" position="bottom" />
                {trakaTabova(0)}
                <div className="faktureStranica">
                    <Card>
                        <div className="sef-ulazne-filter">
                            <div className="sef-filter-polje">
                                <label>{t("izvod.godina")}:</label>
                                <InputNumber value={godina} onValueChange={(e) => setGodina(e.value)}
                                    useGrouping={false} min={2000} max={2100} />
                            </div>
                            <div className="sef-filter-polje">
                                <label>{t("izvod.ziroRacun")}:</label>
                                <CustomDropdown value={filterRacun} optionLabel="label" showClear
                                    options={sifarnici.RacuniDB || []}
                                    placeholder={t("izvod.sviRacuni")}
                                    onChange={(e) => setFilterRacun(e.target.value)} />
                            </div>
                            <div className="sef-filter-dugmad">
                                <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                    className="p-button-sm" loading={ucitava} onClick={ucitajPregled} />
                                <Button type="button" label={t("izvod.novi")} icon="pi pi-plus"
                                    className="p-button-sm p-button-success" onClick={noviIzvod} />
                                <Button type="button" label={t("izvod.uveziXml")} icon="pi pi-upload"
                                    className="p-button-sm p-button-outlined"
                                    onClick={() => fajlRef.current?.click()} />
                                <input ref={fajlRef} type="file" accept=".xml,text/xml"
                                    style={{ display: "none" }} onChange={izaberiFajl} />
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <DataTable value={lista || []} loading={ucitava}
                            paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                            globalFilter={globalFilter} header={headerPregleda}
                            scrollable scrollHeight="55vh" size="small"
                            resizableColumns columnResizeMode="expand"
                            emptyMessage={t("common.noDataTable")}
                            className="turePregledTable stavke-tabela"
                            onRowDoubleClick={(e) => otvoriIzvod(e.data.id)}>
                            <Column header="" style={{ width: "110px" }}
                                body={(r) => (
                                    <Button type="button" label={t("kartica.otvori")} icon="pi pi-eye"
                                        className="p-button-text p-button-sm" onClick={() => otvoriIzvod(r.id)} />
                                )} />
                            <Column field="BrojIzvoda" header={t("izvod.broj")} style={{ minWidth: "90px" }} />
                            <Column field="Godina" header={t("izvod.godina")} style={{ minWidth: "80px" }} />
                            <Column field="DatumIzvoda" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                            <Column field="TekuciRacun" header={t("izvod.ziroRacun")} style={{ minWidth: "160px" }} />
                            <Column header={t("izvod.prethodnoStanje")} style={{ minWidth: "130px" }}
                                body={(r) => <span className="ak-iznos">{iznos(r.PrethodnoStanje)}</span>} />
                            <Column header={t("izvod.dugovniPromet")} style={{ minWidth: "130px" }}
                                body={(r) => <span className="ak-iznos">{iznos(r.DugovniPromet)}</span>} />
                            <Column header={t("izvod.potrazniPromet")} style={{ minWidth: "130px" }}
                                body={(r) => <span className="ak-iznos">{iznos(r.PotrazniPromet)}</span>} />
                            <Column header={t("izvod.novoStanje")} style={{ minWidth: "130px" }}
                                body={(r) => <strong className="ak-iznos">{iznos(r.NovoStanje)}</strong>} />
                            <Column header={t("izvod.stavki")} style={{ minWidth: "150px" }}
                                body={(r) => (
                                    <span className={`sef-pilula ${Number(r.Nesravnjenih) > 0 ? "sef-st-upozorenje" : "sef-st-ok"}`}>
                                        {r.BrojStavki} · {t("izvod.nesravnjenih")} {r.Nesravnjenih}
                                    </span>
                                )} />
                        </DataTable>
                    </Card>
                </div>

                {/* Pregled uvoza — bez upisa, dok operater ne potvrdi. */}
                <Dialog header={t("izvod.uvozNaslov")} visible={!!uvoz} style={{ width: "44rem" }}
                    breakpoints={{ "768px": "95vw" }} onHide={() => setUvoz(null)}>
                    {uvoz?.pregled && (
                        <div className="iz-uvoz">
                            <div className="iz-uvoz-red"><span>{t("izvod.racunIzFajla")}</span><strong>{uvoz.pregled.racunBanke}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.prepoznatRacun")}</span><strong>{uvoz.pregled.nazivRacuna || "—"}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.broj")}</span><strong>{uvoz.pregled.brojIzvoda}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.datum")}</span><strong>{(uvoz.pregled.datumStanja || "").slice(0, 10)}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.brojTransakcija")}</span><strong>{uvoz.pregled.transakcije?.length || 0}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.uplateUkupno")}</span><strong className="ak-plus">{iznos(uvoz.pregled.ukupnoCredit)}</strong></div>
                            <div className="iz-uvoz-red"><span>{t("izvod.isplateUkupno")}</span><strong className="ak-minus">{iznos(uvoz.pregled.ukupnoDebit)}</strong></div>

                            {uvoz.pregled.vecUvezeno > 0 && (
                                <div className="sef-traka upozorenje" style={{ margin: "0.6rem 0 0" }}>
                                    <i className="pi pi-info-circle" />
                                    <span>{t("izvod.vecUvezeno", { broj: uvoz.pregled.vecUvezeno })}</span>
                                </div>
                            )}
                            {!uvoz.pregled.id_tekuci_racun && (
                                <div className="sef-traka upozorenje" style={{ margin: "0.6rem 0 0" }}>
                                    <i className="pi pi-exclamation-triangle" />
                                    <span>{t("izvod.racunNijePrepoznat")}</span>
                                </div>
                            )}

                            <div className="sef-potvrda-dugmad">
                                <Button type="button" label={t("common.cancel")} className="p-button-text"
                                    onClick={() => setUvoz(null)} />
                                <Button type="button" label={t("izvod.uveziPotvrdi")} icon="pi pi-check"
                                    severity="success" disabled={!uvoz.pregled.id_tekuci_racun}
                                    onClick={potvrdiUvoz} />
                            </div>
                        </div>
                    )}
                </Dialog>
            </div>
        );
    }

    // ══════════════════════════ JEDAN IZVOD ══════════════════════════
    return (
        <div className="col-12">
            <Toast ref={toast} /><ConfirmDialog />
            <Tooltip target=".export-buttons>button" position="bottom" />
            {trakaTabova(1)}
            <div className="faktureStranica">

                {/* ── glavna traka radnji ── */}
                <div className="iz-traka">
                    <Button type="button" label={t("common.sacuvaj")} icon="pi pi-save"
                        className="p-button-sm p-button-success"
                        loading={cuva} disabled={zaglavlje.zakljucan} onClick={sacuvajZaglavlje} />
                    <Button type="button" label={t("izvod.sravni")} icon="pi pi-link"
                        className="p-button-sm"
                        disabled={!izabranaStavka || zbirIzbora <= 0 || zaglavlje.zakljucan}
                        onClick={sravni} />
                    <Button type="button" label={t("izvod.poveziRacun")} icon="pi pi-id-card"
                        className="p-button-sm p-button-outlined"
                        disabled={!izabranaStavka || zaglavlje.zakljucan}
                        onClick={() => setKontaktModal({
                            id_izvod_stavka: izabranaStavka.id,
                            id_klijent: izabranaStavka.id_klijent || null,
                            tekuciRacun: izabranaStavka.BrojRacuna || "",
                            nazivBanke: "",
                            klijentNaziv: izabranaStavka.KlijentNaziv || "",
                        })} />
                    {/* Kvačica Zakljucan postoji u bazi i sve radnje je poštuju, ali je
                        do sada ništa nije postavljalo — zaključavanje je bilo mrtvo slovo.
                        Radi u oba smera: pogrešno zaključan izvod inače ostaje zaleđen. */}
                    <Button type="button" icon={zaglavlje.zakljucan ? "pi pi-unlock" : "pi pi-lock"}
                        label={zaglavlje.zakljucan ? t("izvod.otkljucaj") : t("izvod.zakljucaj")}
                        className="p-button-sm p-button-outlined"
                        disabled={!zaglavlje.id} onClick={zakljucaj} />
                    <Button type="button" icon="pi pi-trash" label={t("izvod.obrisiIzvod")}
                        className="p-button-sm p-button-danger p-button-outlined"
                        disabled={!zaglavlje.id || zaglavlje.zakljucan} onClick={obrisiIzvod} />
                    {zaglavlje.zakljucan && (
                        <span className="sef-pilula sef-st-info"><i className="pi pi-lock" /> {t("izvod.zakljucan")}</span>
                    )}
                </div>

                {/* ── zaglavlje izvoda ── */}
                <Card>
                    <div className="iz-zaglavlje-vrh">
                        <p className="iz-podnaslov">{t("izvod.zaglavljeIzvoda")}</p>
                        <Button type="button"
                            icon={zaglavljeOtvoreno ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                            className="p-button-text p-button-sm iz-skupi"
                            aria-label={zaglavljeOtvoreno ? t("izvod.skupiZaglavlje") : t("izvod.prosiriZaglavlje")}
                            title={zaglavljeOtvoreno ? t("izvod.skupiZaglavlje") : t("izvod.prosiriZaglavlje")}
                            onClick={() => setZaglavljeOtvoreno((v) => !v)} />
                    </div>
                    {/* Dve grupe jedna pored druge, kao na starom ekranu: levo podaci
                        o samom izvodu (broj, datum, kurs), desno račun i stanja.
                        Polje „Firma" je izbačeno — firma se zna iz prijave.

                        Labela ostaje IZNAD kontrole, a ne levo kao na staroj slici:
                        to je pravilo cele forme (vidi .ulazne-grid u Fakture.css) jer
                        se dužina labele menja po jeziku, pa bi levo poravnanje kod
                        dužih prevoda razbilo red. */}
                    <div className="iz-zaglavlje-grupe">
                        <div className="iz-grupa iz-grupa-izvod">
                            <div className="iz-red">
                                <div className="iz-polje iz-polje-s">
                                    <label>{t("izvod.broj")}: <span className="iz-obavezno">*</span></label>
                                    <InputNumber value={zaglavlje.brojIzvoda} useGrouping={false}
                                        disabled={zaglavlje.zakljucan}
                                        onValueChange={(e) => setZaglavlje((p) => ({ ...p, brojIzvoda: e.value }))} />
                                </div>
                                <div className="iz-polje iz-polje-xs">
                                    <label>{t("izvod.godina")}:</label>
                                    <InputNumber value={zaglavlje.godina} useGrouping={false}
                                        disabled={zaglavlje.zakljucan}
                                        onValueChange={(e) => setZaglavlje((p) => ({ ...p, godina: e.value }))} />
                                </div>
                            </div>

                            {zaglavljeOtvoreno && (
                                <>
                                    <div className="iz-red">
                                        <div className="iz-polje iz-polje-m">
                                            <label>{t("izvod.datum")}: <span className="iz-obavezno">*</span></label>
                                            <Calendar value={zaglavlje.datumIzvoda} dateFormat="dd.mm.yy." showIcon
                                                disabled={zaglavlje.zakljucan}
                                                onChange={(e) => promeniDatum(e.value)} />
                                        </div>
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("ulazne.valuta")}: <span className="iz-obavezno">*</span></label>
                                            <CustomDropdown value={zaglavlje.valuta} optionLabel="label"
                                                options={sifarnici.ValuteDB || []} disabled={zaglavlje.zakljucan}
                                                placeholder={t("ulazne.phValuta")}
                                                onChange={(e) => promeniValutu(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="iz-red">
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.kurs")}:</label>
                                            <InputNumber value={zaglavlje.kurs} mode="decimal"
                                                minFractionDigits={4} maxFractionDigits={4} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, kurs: e.value }))} />
                                            {kursDatum && (
                                                <small className="iz-kurs-napomena">
                                                    {t("izvod.kursSaDana", { datum: kursDatum })}
                                                </small>
                                            )}
                                        </div>
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.kursValuta")}:</label>
                                            <InputNumber value={zaglavlje.kursValuta} mode="decimal"
                                                minFractionDigits={4} maxFractionDigits={4} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, kursValuta: e.value }))} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="iz-grupa iz-grupa-racun">
                            <div className="iz-red">
                                {/* Žiro račun ide preko oba polja ispod sebe — broj računa
                                    se ne skraćuje, a red ostaje poravnat sa stanjima. */}
                                <div className="iz-polje iz-polje-duplo">
                                    <label>{t("izvod.ziroRacun")}: <span className="iz-obavezno">*</span></label>
                                    <CustomDropdown value={zaglavlje.id_tekuci_racun} optionLabel="label"
                                        options={sifarnici.RacuniDB || []} disabled={zaglavlje.zakljucan}
                                        placeholder={t("izvod.izaberiRacun")}
                                        onChange={(e) => promeniRacun(e.target.value)} />
                                </div>
                            </div>

                            {zaglavljeOtvoreno && (
                                <>
                                    <div className="iz-red">
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.prethodnoStanje")}:</label>
                                            <InputNumber value={zaglavlje.prethodnoStanje} mode="decimal"
                                                minFractionDigits={2} maxFractionDigits={2} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, prethodnoStanje: e.value }))} />
                                        </div>
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.dugovniPromet")}:</label>
                                            <InputNumber value={zaglavlje.dugovniPromet} mode="decimal"
                                                minFractionDigits={2} maxFractionDigits={2} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, dugovniPromet: e.value }))} />
                                        </div>
                                    </div>
                                    <div className="iz-red">
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.novoStanje")}:</label>
                                            <InputNumber value={zaglavlje.novoStanje} mode="decimal"
                                                minFractionDigits={2} maxFractionDigits={2} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, novoStanje: e.value }))} />
                                        </div>
                                        <div className="iz-polje iz-polje-s">
                                            <label>{t("izvod.potrazniPromet")}:</label>
                                            <InputNumber value={zaglavlje.potrazniPromet} mode="decimal"
                                                minFractionDigits={2} maxFractionDigits={2} disabled={zaglavlje.zakljucan}
                                                onValueChange={(e) => setZaglavlje((p) => ({ ...p, potrazniPromet: e.value }))} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Napomena puni prostor koji ostaje desno od stanja — zato je
                            treća grupa, a ne red ispod: ispod bi dodala još jedan red
                            visine, a cilj je da zaglavlje bude niže. */}
                        {zaglavljeOtvoreno && (
                            <div className="iz-grupa iz-grupa-napomena">
                                <div className="iz-polje">
                                    <label>{t("nova.napomena")}:</label>
                                    <InputTextarea value={zaglavlje.napomena || ""}
                                        disabled={zaglavlje.zakljucan}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, napomena: e.target.value }))} />
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* ── PRVA TABELA: stavke izvoda ── */}
                <Card>
                    <div className="iz-alat">
                        <span className="iz-podnaslov">{t("izvod.stavkeIzvoda")}</span>
                        <Button type="button" icon="pi pi-plus" label={t("izvod.novaStavka")}
                            className="p-button-sm p-button-success" disabled={!zaglavlje.id || zaglavlje.zakljucan}
                            onClick={() => setStavkaModal({ ...praznaStavka })} />
                        <Button type="button" icon="pi pi-pencil" label={t("common.edit")}
                            className="p-button-sm p-button-outlined" disabled={!izabranaStavka || zaglavlje.zakljucan}
                            onClick={() => setStavkaModal({
                                id_stavka: izabranaStavka.id,
                                id_klijent: izabranaStavka.id_klijent,
                                klijentNaziv: izabranaStavka.KlijentNaziv || "",
                                duguje: izabranaStavka.Duguje, potrazuje: izabranaStavka.Potrazuje,
                                brojRacuna: izabranaStavka.BrojRacuna || "",
                                sifraPlacanja: izabranaStavka.SifraPlacanja || "",
                                opisPlacanja: izabranaStavka.OpisPlacanja || "",
                                modelOdobrenja: izabranaStavka.ModelOdobrenja || "",
                                pozivOdobrenja: izabranaStavka.PozivOdobrenja || "",
                                vrstaStavke: izabranaStavka.VrstaStavke,
                                id_vozac: izabranaStavka.id_vozac,
                                id_vrsta_troska: izabranaStavka.id_vrsta_troska,
                                id_povezano_lice: izabranaStavka.id_povezano_lice,
                                opisKUF: izabranaStavka.OpisKUF || "",
                                napomena: izabranaStavka.Napomena || "",
                            })} />
                        <Button type="button" icon="pi pi-trash" label={t("common.delete")}
                            className="p-button-sm p-button-danger p-button-outlined"
                            disabled={!izabranaStavka || zaglavlje.zakljucan} onClick={obrisiStavku} />

                        <div className="iz-filter-klijent">
                            <label>{t("izvod.klijent")}:</label>
                            <CustomDropdown value={filterKlijent} optionLabel="label" showClear filter
                                options={sifarnici.KlijentiDB || []}
                                placeholder={t("izvod.sviKlijenti")}
                                onChange={(e) => setFilterKlijent(e.target.value)} />
                        </div>

                        {/* Legenda: boja reda nosi isto što i tekst, za brzo skeniranje. */}
                        <span className="iz-legenda">
                            <span className="iz-lg iz-lg-trosak" /> {t("izvod.lgTrosak")}
                            <span className="iz-lg iz-lg-sravnjen" /> {t("izvod.lgSravnjene")}
                            <span className="iz-lg iz-lg-vozac" /> {t("izvod.lgVozac")}
                            <span className="iz-lg iz-lg-nesravnjen" /> {t("izvod.lgNesravnjene")}
                        </span>
                    </div>

                    <DataTable value={prikazaneStavke} size="small" showGridlines
                        globalFilter={filterStavki} header={headerStavki}
                        scrollable scrollHeight="30vh" emptyMessage={t("common.noDataTable")}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        selectionMode="single" selection={izabranaStavka}
                        onSelectionChange={(e) => izaberiStavku(e.value)}
                        rowClassName={klasaStavke}
                        footerColumnGroup={footerStavki}>
                        <Column header={t("izvod.klijent")} style={{ minWidth: "210px" }}
                            body={(r) => r.KlijentIzSifarnika || r.KlijentNaziv || "—"} />
                        <Column field="BrojStavke" header={t("izvod.brStavke")} style={{ minWidth: "100px" }} />
                        <Column field="SifraPlacanja" header={t("izvod.sifraPlacanja")} style={{ minWidth: "110px" }} />
                        <Column header={t("izvod.dugujePlaceno")} style={{ minWidth: "140px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Duguje)}</span>} />
                        <Column header={t("izvod.potrazujeNaplaceno")} style={{ minWidth: "140px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Potrazuje)}</span>} />
                        <Column field="PozivOdobrenja" header={t("izvod.pozivNaBroj")} style={{ minWidth: "130px" }} />
                        <Column field="OpisPlacanja" header={t("izvod.opisPlacanja")} style={{ minWidth: "200px" }} />
                        <Column field="PovezanoLice" header={t("izvod.povezanoLice")} style={{ minWidth: "150px" }} />
                        <Column field="OpisKUF" header={t("izvod.opisKUF")} style={{ minWidth: "140px" }} />
                        <Column field="Napomena" header={t("nova.napomena")} style={{ minWidth: "160px" }} />
                    </DataTable>
                </Card>

                {/* ── DRUGA TABELA: fakture partnera koje se zatvaraju ── */}
                <Card>
                    <div className="iz-alat">
                        <span className="iz-podnaslov">{t("izvod.otvoreneStavke")}</span>
                        <Button type="button" icon="pi pi-bolt" label={t("izvod.predlozi")}
                            className="p-button-sm p-button-outlined"
                            disabled={!izabranaStavka} onClick={predlozi} />
                        <Button type="button" icon="pi pi-link" label={t("izvod.sravni")}
                            className="p-button-sm p-button-success"
                            disabled={!izabranaStavka || zbirIzbora <= 0 || zaglavlje.zakljucan}
                            onClick={sravni} />

                        {/* Suma označenog mora stajati uz dugme — poredi se sa iznosom stavke
                            pre nego što se sravnjenje potvrdi. */}
                        <div className="iz-izabrana-suma">
                            <label>{t("izvod.trenutnaSuma")}:</label>
                            <InputText value={iznos(zbirIzbora)} readOnly
                                className={zbirIzbora > preostaloNaStavci + 0.005 ? "iz-suma-preko" : ""} />
                            {izabranaStavka && (
                                <span className="iz-od">
                                    / {iznos(preostaloNaStavci)} {t("izvod.naStavci")}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Stavka bez partnera: upit bez klijenta vratio bi SVE otvorene
                        fakture firme, pa se spisak namerno ne puni — ali tabela ostaje
                        na ekranu, sa kolonama, da se vidi gde će se podaci pojaviti. */}
                    {izabranaStavka && !izabranaStavka.id_klijent && (
                        <div className="sef-traka upozorenje">
                            <i className="pi pi-exclamation-triangle" />
                            <span>{t("izvod.stavkaBezPartnera")}</span>
                        </div>
                    )}

                    <DataTable value={otvorene || []} loading={ucitavaOtvorene}
                        size="small" showGridlines scrollable scrollHeight="28vh"
                        resizableColumns columnResizeMode="expand"
                        emptyMessage={!izabranaStavka
                            ? t("izvod.izaberiStavkuZaSravnjenje")
                            : (!izabranaStavka.id_klijent
                                ? t("izvod.stavkaBezPartnera")
                                : t("izvod.nemaOtvorenih"))}
                        className="turePregledTable"
                        rowClassName={(r) => ({ "iz-red-dospela": r.Dospela === 1 })}>
                        <Column header={t("izvod.sravni")} style={{ width: "80px" }}
                            body={(r) => (
                                <Checkbox checked={Number(izborZaSravnjenje[r.id_dokument] || 0) > 0}
                                    disabled={zaglavlje.zakljucan}
                                    onChange={(e) => prekidacReda(r, e.checked)} />
                            )} />
                        <Column header={t("izvod.ukupnoIznos")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Ukupno)}</span>} />
                        <Column header={t("izvod.rezervisano")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Rezervisano)}</span>} />
                        <Column header={t("izvod.zaSravnjenje")} style={{ minWidth: "160px" }}
                            body={(r) => (
                                <InputNumber value={izborZaSravnjenje[r.id_dokument] ?? r.IznosZaSravnjenje}
                                    mode="decimal" minFractionDigits={2} maxFractionDigits={2}
                                    inputStyle={{ width: "9rem", textAlign: "right" }}
                                    disabled={zaglavlje.zakljucan}
                                    onValueChange={(e) =>
                                        setIzborZaSravnjenje((p) => {
                                            const n = { ...p };
                                            if (e.value === null || Number(e.value) === 0) delete n[r.id_dokument];
                                            else n[r.id_dokument] = e.value;
                                            return n;
                                        })} />
                            )} />
                        <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "190px" }} />
                        <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                        <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                        <Column field="Rok" header={t("ulazne.rokPlacanja")} style={{ minWidth: "110px" }} />
                        <Column field="Napomena" header={t("nova.napomena")} style={{ minWidth: "160px" }} />
                        <Column header={t("izvod.napomenaSravnjivanje")} style={{ minWidth: "200px" }}
                            body={(r) => (
                                <InputText value={napomeneSravnjenja[r.id_dokument] ?? (r.NapomenaSravnjivanje || "")}
                                    disabled={zaglavlje.zakljucan}
                                    onChange={(e) =>
                                        setNapomeneSravnjenja((p) => ({ ...p, [r.id_dokument]: e.target.value }))} />
                            )} />
                        <Column field="NasaRef" header={t("izvod.nasaRef")} style={{ minWidth: "130px" }} />
                        <Column field="VrstaFakture" header={t("izvod.vrstaFakture")} style={{ minWidth: "150px" }} />
                </DataTable>

                    {/* Već sravnjeno na ovoj stavci — sa mogućnošću poništavanja. */}
                    {izabranaStavka && sravnjenja.length > 0 && (
                        <>
                            <p className="iz-podnaslov" style={{ marginTop: "0.8rem" }}>{t("izvod.vecSravnjeno")}</p>
                            <DataTable value={sravnjenja} size="small" showGridlines
                                className="turePregledTable" scrollable scrollHeight="18vh"
                                resizableColumns columnResizeMode="expand">
                                <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                                <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "190px" }} />
                                <Column header={t("izvod.iznos")} style={{ minWidth: "130px" }}
                                    body={(r) => <span className="ak-iznos">{iznos(r.Iznos)}</span>} />
                                <Column field="Napomena" header={t("izvod.napomenaSravnjivanje")} style={{ minWidth: "180px" }} />
                                <Column field="DatumSravnjenja" header={t("izvod.datumSravnjenja")} style={{ minWidth: "150px" }} />
                                <Column field="Korisnik" header={t("izvod.korisnik")} style={{ minWidth: "150px" }} />
                                <Column header="" style={{ width: "120px" }}
                                    body={(r) => (
                                        <Button type="button" icon="pi pi-undo" label={t("izvod.ponisti")}
                                            className="p-button-text p-button-sm p-button-danger"
                                            disabled={zaglavlje.zakljucan} onClick={() => ponisti(r)} />
                                    )} />
                            </DataTable>
                        </>
                    )}
                </Card>
            </div>

            {/* ── modal stavke ── */}
            <Dialog header={stavkaModal?.id_stavka ? t("izvod.izmenaStavke") : t("izvod.novaStavka")}
                visible={!!stavkaModal} style={{ width: "58rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setStavkaModal(null)}>
                {stavkaModal && (
                    <div className="gridFakture ulazne-grid">
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.klijent")}:</label>
                            <CustomDropdown value={stavkaModal.id_klijent} optionLabel="label" showClear filter
                                options={sifarnici.KlijentiDB || []}
                                placeholder={t("ulazne.phDobavljac")}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, id_klijent: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.klijentIzBanke")}:</label>
                            <InputText value={stavkaModal.klijentNaziv}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, klijentNaziv: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.dugujePlaceno")}:</label>
                            <InputNumber value={stavkaModal.duguje} mode="decimal"
                                minFractionDigits={2} maxFractionDigits={2}
                                onValueChange={(e) => setStavkaModal((p) => ({ ...p, duguje: e.value, potrazuje: e.value ? null : p.potrazuje }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.potrazujeNaplaceno")}:</label>
                            <InputNumber value={stavkaModal.potrazuje} mode="decimal"
                                minFractionDigits={2} maxFractionDigits={2}
                                onValueChange={(e) => setStavkaModal((p) => ({ ...p, potrazuje: e.value, duguje: e.value ? null : p.duguje }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.sifraPlacanja")}:</label>
                            <InputText value={stavkaModal.sifraPlacanja}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, sifraPlacanja: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.pozivNaBroj")}:</label>
                            <InputText value={stavkaModal.pozivOdobrenja}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, pozivOdobrenja: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.brojRacunaPartnera")}:</label>
                            <InputText value={stavkaModal.brojRacuna}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, brojRacuna: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.vrstaStavke")}:</label>
                            <CustomDropdown value={stavkaModal.vrstaStavke} optionLabel="label" showClear
                                options={sifarnici.VrsteStavkeDB || []}
                                placeholder={t("izvod.phVrstaStavke")}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, vrstaStavke: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.povezanoLice")}:</label>
                            <CustomDropdown value={stavkaModal.id_povezano_lice} optionLabel="label" showClear filter
                                options={sifarnici.ZaposleniDB || []}
                                placeholder={t("izvod.phPovezanoLice")}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, id_povezano_lice: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.vozac")}:</label>
                            <CustomDropdown value={stavkaModal.id_vozac} optionLabel="label" showClear filter
                                options={sifarnici.VozaciDB || []}
                                placeholder={t("izvod.phVozac")}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, id_vozac: e.target.value }))} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("izvod.vrstaTroska")}:</label>
                            <CustomDropdown value={stavkaModal.id_vrsta_troska} optionLabel="label" showClear
                                options={sifarnici.VrsteTroskaDB || []}
                                placeholder={t("izvod.phVrstaTroska")}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, id_vrsta_troska: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.opisPlacanja")}:</label>
                            <InputText value={stavkaModal.opisPlacanja}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, opisPlacanja: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.opisKUF")}:</label>
                            <InputText value={stavkaModal.opisKUF}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, opisKUF: e.target.value }))} />
                        </div>
                        <div className="polje-puno col-12">
                            <label>{t("nova.napomena")}:</label>
                            <InputTextarea rows={2} value={stavkaModal.napomena}
                                onChange={(e) => setStavkaModal((p) => ({ ...p, napomena: e.target.value }))} />
                        </div>
                        <div className="polje-puno col-12">
                            <div className="sef-potvrda-dugmad">
                                <Button type="button" label={t("common.cancel")} className="p-button-text"
                                    onClick={() => setStavkaModal(null)} />
                                <Button type="button" label={t("common.sacuvaj")} icon="pi pi-save"
                                    severity="success" onClick={sacuvajStavku} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ── modal "Novi kontakt": broj računa → partner ── */}
            <Dialog header={t("izvod.poveziRacun")} visible={!!kontaktModal} style={{ width: "40rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setKontaktModal(null)}>
                {kontaktModal && (
                    <div className="gridFakture ulazne-grid">
                        <div className="polje-puno col-12">
                            <div className="sef-ulazne-napomena" style={{ marginTop: 0 }}>
                                <i className="pi pi-info-circle" /> {t("izvod.poveziRacunOpis")}
                            </div>
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.klijentIzBanke")}:</label>
                            <InputText value={kontaktModal.klijentNaziv} readOnly disabled />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.brojRacunaPartnera")}:</label>
                            <InputText value={kontaktModal.tekuciRacun}
                                onChange={(e) => setKontaktModal((p) => ({ ...p, tekuciRacun: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.klijent")}:</label>
                            <CustomDropdown value={kontaktModal.id_klijent} optionLabel="label" showClear filter
                                options={sifarnici.KlijentiDB || []}
                                placeholder={t("ulazne.phDobavljac")}
                                onChange={(e) => setKontaktModal((p) => ({ ...p, id_klijent: e.target.value }))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("izvod.nazivBanke")}:</label>
                            <InputText value={kontaktModal.nazivBanke}
                                onChange={(e) => setKontaktModal((p) => ({ ...p, nazivBanke: e.target.value }))} />
                        </div>
                        <div className="polje-puno col-12">
                            <div className="sef-potvrda-dugmad">
                                <Button type="button" label={t("common.cancel")} className="p-button-text"
                                    onClick={() => setKontaktModal(null)} />
                                <Button type="button" label={t("common.sacuvaj")} icon="pi pi-save"
                                    severity="success"
                                    disabled={!kontaktModal.id_klijent || !kontaktModal.tekuciRacun}
                                    onClick={veziRacun} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default Izvodi;
